import React, { useState } from 'react';
import { Box, Container, Typography, Button, Paper, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SignupSuccess: React.FC = () => {
  const navigate = useNavigate();
  const pendingEmail = localStorage.getItem('pendingVerificationEmail');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleResendVerification = async () => {
    if (!pendingEmail) {
      setError('No email found for verification. Please register again.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: pendingEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      setSuccess('Verification email has been resent. Please check your inbox.');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend verification email';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              Registration Successful!
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Typography variant="body1" paragraph>
              Thank you for registering with Scene Detector! We've sent a verification email to:
            </Typography>

            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
              {pendingEmail}
            </Typography>

            <Typography variant="body1" paragraph>
              Please check your inbox and click the verification link to activate your account. The link will expire in 24 hours.
            </Typography>

            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleResendVerification}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                {isLoading ? 'Resending...' : 'Resend Verification Email'}
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none' }}
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default SignupSuccess; 